import type { UserCommunicationAgent } from './userCommunicationAgent';

export interface FlowGroup {
  groupId: string;
  userId: string;
  teamId?: string;
  name: string;
  objective?: string;
  description?: string;
  flowIds: string[];
  isActive: boolean;
  icon?: string;
  chatMessages?: string;
  conversationMessages?: string;
  templateId?: string;
  createdAt: number;
  updatedAt: number;
}

export interface FlowGroupWithFlows {
  group: FlowGroup;
  flows: UserCommunicationAgent[];
}

export enum GenerationMode {
  SINGLE_FLOW = 'SINGLE_FLOW',
  FLOW_GROUP = 'FLOW_GROUP',
  CLARIFICATION = 'CLARIFICATION',
  COMPLEXITY_UPGRADE_SUGGESTION = 'COMPLEXITY_UPGRADE_SUGGESTION',
  GROUP_PATCH = 'GROUP_PATCH',
}

export enum GenerationContext {
  FLOW_EDITOR = 'FLOW_EDITOR',
  GROUP_BUILDER = 'GROUP_BUILDER',
  GROUP_EDIT = 'GROUP_EDIT',
}

export const REVIEW_CONFIRMATION_QUESTION_ID = 'confirm_creation';

export enum ReviewConfirmationOption {
  CREATE = 'Yes, create the agent',
  UPDATE = 'Yes, update the agent',
  REQUEST_CHANGES = 'No, I want to make changes',
}

export enum SystemEventType {
  FLOW_MOVED_IN = 'flow_moved_in',
  FLOW_MOVED_OUT = 'flow_moved_out',
  FLOW_CREATED = 'flow_created',
  FLOW_DELETED = 'flow_deleted',
  GROUP_ACTIVATED = 'group_activated',
  GROUP_PAUSED = 'group_paused',
  GROUP_METADATA_UPDATED = 'group_metadata_updated',
}

export interface ComplexityUpgrade {
  reason: string;
  proposedFlowCount: number;
  proposedFlowSummaries: string[];
}

export interface GeneratedFlow {
  name: string;
  trigger: Record<string, unknown>;
  steps: Record<string, unknown>[];
  planSteps?: string[];
  scheduleDescription?: string;
}

export interface GeneratedFlowGroup {
  name: string;
  objective: string;
  description?: string;
  flows: GeneratedFlow[];
  // Sub-agent templates proposed alongside this group, shared across its
  // flows. Untyped here — the generator owns the precise GeneratedAgentTemplate
  // shape and this field just carries it through to the frontend.
  newAgentTemplates?: unknown[];
}

export interface FlowUpdate {
  flowId: string;
  changes: Partial<GeneratedFlow>;
}

export interface GroupMetadataUpdate {
  name?: string;
  objective?: string;
  description?: string;
}

export interface GroupPatch {
  addFlows?: GeneratedFlow[];
  removeFlowIds?: string[];
  updateFlows?: FlowUpdate[];
  updateGroup?: GroupMetadataUpdate;
  // Sub-agent templates proposed alongside this patch's addFlows/updateFlows.
  newAgentTemplates?: unknown[];
}

export enum FlowGroupKeyPart {
  FLOW_GROUP = 'FLOW_GROUP',
  METADATA = 'METADATA',
  USER = 'USER',
  ID = 'ID',
}
